import React, { useState, useEffect } from "react"
import { useDebounce } from "../hooks/useDebounce"
import { VStack, HStack, Input, Text } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { DateTime, utc, zone } from "timezonecomplete"

import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "../utils/toast"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import { OrderWorkflowProvider } from "../contexts/OrderWorkflowContext"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { validateQuantity } from "../utils/quantityValidation"
import { CollectionSection, validateCollectionInput } from "./CollectionSection"

interface OrderDetailsEditorProps {
  order: gqlTypes.Order
  onSuccess: () => void | Promise<void>
  onCancel: () => void
  hasStartedActivities: boolean
  allActivities: gqlTypes.Activity[] // Not currently used but passed for future needs
}

// Format existing dates for input fields (convert from ISO to yyyy-MM-dd)
const getDefaultDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return ""
  try {
    const tz = zone(IANATimeZone())

    // Handle dates that come without timezone info (treat as UTC)
    let dt: DateTime
    if (!isoDate.includes("Z") && !isoDate.includes("+") && !isoDate.includes("-", 10)) {
      dt = new DateTime(isoDate, utc()).toZone(tz)
    } else {
      dt = new DateTime(isoDate).toZone(tz)
    }

    return dt.format("yyyy-MM-dd")
  } catch (e) {
    console.error("Error parsing date:", e, isoDate)
    return ""
  }
}

const OrderDetailsEditorInner: React.FC<OrderDetailsEditorProps> = ({
  order,
  onSuccess,
  onCancel,
  hasStartedActivities,
  allActivities,
}) => {
  const [error, setError] = useState<string | null>(null)
  const activityless = useGetFeature("orders_activityless")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.ASSET]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSET],
    [COPY_KEYS.HEADINGS.ORDER_QUANTITY]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_QUANTITY],
  })

  // Use controlled state for inputs
  const [startDate, setStartDate] = useState<string>(() => getDefaultDate(order.start))
  const [endDate, setEndDate] = useState<string>(() => getDefaultDate(order.end))
  const [quantity, setQuantity] = useState<string>(order.quantity?.toString() || "")
  const [quantityConsumed, setQuantityConsumed] = useState<string>(order.quantity_consumed?.toString() ?? "")
  const [quantityErrors, setQuantityErrors] = useState<{ consumed?: string }>({})
  const [dateValidationError, setDateValidationError] = useState<string | null>(null)

  // Collection replacement state - pre-populated from existing order data
  const [collectionReplacement, setCollectionReplacement] = useState<boolean>(
    order.collection_replacement ?? false
  )
  const [collectionQuantityCollected, setCollectionQuantityCollected] = useState<string>(
    !(order.collection_replacement) && order.quantity_collected != null
      ? order.quantity_collected.toString()
      : ""
  )
  const [collectionQuantityError, setCollectionQuantityError] = useState<string | null>(null)

  // Debounce the date values to prevent excessive validation
  const debouncedStartDate = useDebounce(startDate, 250)
  const debouncedEndDate = useDebounce(endDate, 250)

  // Update state when order changes (e.g., when dialog is reopened with different order)
  useEffect(() => {
    setStartDate(getDefaultDate(order.start))
    setEndDate(getDefaultDate(order.end))
    setDateValidationError(null)
    setQuantity(order.quantity?.toString() || "")
    setQuantityConsumed(order.quantity_consumed?.toString() ?? "")
    setQuantityErrors({})
    setCollectionReplacement(order.collection_replacement ?? false)
    setCollectionQuantityCollected(
      !(order.collection_replacement) && order.quantity_collected != null
        ? order.quantity_collected.toString()
        : ""
    )
    setCollectionQuantityError(null)
  }, [order.id, order.start, order.end, order.quantity, order.quantity_collected, order.quantity_consumed, order.collection_replacement])

  // Validate dates whenever they change (using debounced values)
  useEffect(() => {
    if (debouncedStartDate && debouncedEndDate) {
      try {
        const tz = zone(IANATimeZone())
        const start = new DateTime(debouncedStartDate, "YYYY-MM-DD", tz)
        const end = new DateTime(debouncedEndDate, "YYYY-MM-DD", tz)

        if (end.valueOf() < start.valueOf()) {
          setDateValidationError("End date cannot be before start date")
        } else {
          setDateValidationError(null)
        }
      } catch {
        // Invalid date format, clear validation error
        setDateValidationError(null)
      }
    } else {
      setDateValidationError(null)
    }
  }, [debouncedStartDate, debouncedEndDate])

  const [updateOrder, { loading }] = useMutationWithEndpoint(
    gqlTypes.UpdateOrderDocument,
    {
      update: (cache, { data }) => {
        if (!data?.updateOrder) return

        // Evict all activities queries for this order to force refetch
        // This ensures activities reflect the updated asset list
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: "getActivities",
          args: { order: order.id },
        })

        // Clean up dangling references
        cache.gc()
      },
    },
    "primary"
  )

  const handleSubmit = async (): Promise<boolean> => {
    let toastId: string | undefined
    try {
      setError(null)

      // Check for validation errors before submitting
      if (dateValidationError) {
        setError(dateValidationError)
        showErrorToast("Validation Error", dateValidationError)
        return false
      }

      toastId = showLoadingToast(`Updating ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} details...`)

      const tz = zone(IANATimeZone())
      const vars: gqlTypes.UpdateOrderMutationVariables = {
        id: order.id,
      }

      // Parse, validate, and format dates if provided
      if (startDate && endDate) {
        try {
          const start = new DateTime(startDate, "YYYY-MM-DD", tz)
          const end = new DateTime(endDate, "YYYY-MM-DD", tz)

          // Validate end is not before start
          if (end.valueOf() < start.valueOf()) {
            if (toastId) dismissToast(toastId)
            const errorMessage = "End date cannot be before start date"
            setError(errorMessage)
            showErrorToast("Validation Error", errorMessage)
            return false
          }

          vars.start = start.toZone(utc()).toIsoString()
          vars.end = end.toZone(utc()).toIsoString()
        } catch (error) {
          if (toastId) dismissToast(toastId)
          const errorMessage = "Invalid date format. Please check your dates."
          setError(errorMessage)
          showErrorToast("Validation Error", errorMessage)
          return false
        }
      }

      if (quantity) {
        try {
          vars.quantity = parseFloat(quantity)
        } catch (error) {
          if (toastId) dismissToast(toastId)
          const errorMessage = "Invalid quantity format. Please check your quantity."
          setError(errorMessage)
          showErrorToast("Validation Error", errorMessage)
          return false
        }
      }

      // Include quantity_consumed when activityless mode is on
      if (activityless) {
        const consumedError = validateQuantity(quantityConsumed)

        const newErrors: { consumed?: string } = {}
        if (consumedError) newErrors.consumed = consumedError

        setQuantityErrors(newErrors)

        if (Object.keys(newErrors).length > 0) {
          if (toastId) dismissToast(toastId)
          return false
        }

        if (quantityConsumed !== "") {
          vars.quantity_consumed = parseFloat(quantityConsumed)
        }
      }

      // Validate and include collection replacement fields
      const collectionValidationError = validateCollectionInput(collectionReplacement, collectionQuantityCollected)
      if (collectionValidationError) {
        setCollectionQuantityError(collectionValidationError)
        if (toastId) dismissToast(toastId)
        return false
      }
      vars.collection_replacement = collectionReplacement
      if (collectionReplacement) {
        vars.quantity_collected = null
      } else if (collectionQuantityCollected !== "") {
        vars.quantity_collected = parseFloat(collectionQuantityCollected)
      }

      const { data: updateData } = await updateOrder({
        variables: vars,
      })

      if (updateData?.updateOrder) {
        dismissToast(toastId)
        showSuccessToast(`${copy[COPY_KEYS.HEADINGS.ORDER]} details updated successfully`)
        await onSuccess()
        return true
      } else {
        setError(`Failed to update ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} details`)
        showErrorToast(`Failed to update ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} details`)
        return false
      }
    } catch (error: any) {
      console.error(`Error updating ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} details:`, error)
      if (toastId) {
        dismissToast(toastId)
      }
      // Show user-friendly error message
      const errorMessage =
        error?.message || `Unable to update ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} details. Please try again.`
      setError(errorMessage)
      showErrorToast("Update Failed", errorMessage)
      return false
    }
  }

  return (
    <Form onBack={onCancel} actionType="confirm" onSubmit={handleSubmit} loading={loading}>
      <VStack gap={4} align="stretch">
        {(error || dateValidationError) && <ErrorAlert title="Error" message={error || dateValidationError || ""} />}

        <Text fontSize="sm" color="fg.muted">
          Update the {copy[COPY_KEYS.HEADINGS.ASSET]} and scheduled dates for this{" "}
          {copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}.
        </Text>

        {hasStartedActivities && (
          <Text fontSize="sm" color="orange.600" fontWeight="semibold">
            Note: Some activities have been started or completed. You cannot modify dates or asset assignment.
          </Text>
        )}

        <VStack gap={2} width="100%" justifyContent="space-between">
          <HStack gap={2} width="100%">
            <Field label="Start Date" required>
              <Input
                name="start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={hasStartedActivities}
              />
            </Field>
            <Field label="End Date" required>
              <Input
                name="end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={hasStartedActivities}
              />
            </Field>
          </HStack>
          <Field label={`${copy[COPY_KEYS.HEADINGS.ORDER_QUANTITY]}`}>
            <Input
              name="quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={hasStartedActivities}
            />
          </Field>

          {activityless && (
            <>
              <Field
                label="Consumed (kg)"
                invalid={!!quantityErrors.consumed}
                errorText={quantityErrors.consumed}
              >
                <Input
                  type="number"
                  value={quantityConsumed}
                  onChange={(e) => {
                    setQuantityConsumed(e.target.value)
                    if (quantityErrors.consumed) setQuantityErrors((prev) => ({ ...prev, consumed: undefined }))
                  }}
                  placeholder="0.00"
                  disabled={hasStartedActivities}
                  data-testid="quantity-consumed-input"
                />
              </Field>
            </>
          )}

          <CollectionSection
            collectionReplacement={collectionReplacement}
            quantityCollected={collectionQuantityCollected}
            onCollectionReplacementChange={(value) => {
              setCollectionReplacement(value)
              if (value) {
                setCollectionQuantityCollected("")
                setCollectionQuantityError(null)
              }
            }}
            onQuantityCollectedChange={(value) => {
              setCollectionQuantityCollected(value)
              if (collectionQuantityError) setCollectionQuantityError(null)
            }}
            quantityCollectedError={collectionQuantityError}
          />
        </VStack>
      </VStack>
    </Form>
  )
}

// Wrap with context provider
const OrderDetailsEditor: React.FC<OrderDetailsEditorProps> = (props) => {
  return (
    <OrderWorkflowProvider>
      <OrderDetailsEditorInner {...props} />
    </OrderWorkflowProvider>
  )
}

export default OrderDetailsEditor
